import { IconButton as MuiIconButton } from '@mui/material';
import { iconButtonClasses, IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

export type { IconButtonProps };

export const IconButton: React.ComponentType<IconButtonProps> = styled(
  MuiIconButton,
)<IconButtonProps>(({ theme, size, color }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  borderRadius: '32px',
  [`&:active`]: {
    transition: 'ease-in-out 0.1s',
    transform: 'scaleX(0.975) scaleY(0.975)',
  },
  [`&.${iconButtonClasses.disabled}`]: {
    backgroundColor: theme.palette.colors.bg['neutral-secondary'],
    color: theme.palette.colors.content.tertiary,
  },
  ...(size === 'large' && {
    padding: '16px',
    [`.MuiCircularProgress-root`]: {
      width: '24px!important',
      height: '24px!important',
    },
  }),
  ...(size === 'medium' && {
    padding: '12px',
    [`&.${iconButtonClasses.loadingIndicator}`]: {
      width: '24px',
      height: '24px',
    },
    [`.MuiCircularProgress-root`]: {
      width: '24px!important',
      height: '24px!important',
    },
  }),
  ...(size === 'small' && {
    padding: '8px',
    [`.MuiSvgIcon-root`]: {
      width: '20px',
      height: '20px',
    },
    [`.MuiCircularProgress-root`]: {
      width: '20px!important',
      height: '20px!important',
    },
  }),
  ...(color === 'primary' && {
    color: theme.palette.colors.core.white,
    background: theme.palette.colors.bg['action-primary'],
    [`&:hover`]: {
      background: theme.palette.colors['bg-active']['action-primary'],
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      background: theme.palette.colors.bg['action-primary'],
      color: theme.palette.colors.bg['action-primary'],
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.core.white,
    },
  }),
  ...(color === 'danger' && {
    color: theme.palette.colors.core.white,
    background: theme.palette.colors.bg['danger-primary'],
    [`&:hover`]: {
      background: theme.palette.colors['bg-active']['danger-primary'],
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      background: theme.palette.colors.bg['danger-primary'],
      color: theme.palette.colors.bg['danger-primary'],
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.core.white,
    },
  }),
  ...(color === 'secondary' && {
    color: theme.palette.colors.content.primary,
    background: theme.palette.colors.bg['neutral-secondary'],
    [`&:hover`]: {
      background: theme.palette.colors['bg-active']['neutral-secondary'],
    },
    [`&.${iconButtonClasses.disabled}`]: {
      backgroundColor: 'none',
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      color: theme.palette.colors.bg['neutral-secondary'],
      background: theme.palette.colors.bg['neutral-secondary'],
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.content.primary,
    },
  }),
  ...(color === 'ghost' && {
    color: theme.palette.colors.content.primary,
    background: 'none',
    [`&:hover`]: {
      background: theme.palette.colors.bg['neutral-secondary'],
    },
    [`&.${iconButtonClasses.disabled}`]: {
      backgroundColor: 'none',
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      color: 'none',
      background: 'none',
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.content.primary,
    },
  }),
  ...(color === 'inverted' && {
    color: theme.palette.colors.core.white,
    background: theme.palette.colors.bg['neutral-primary'],
    [`&:hover`]: {
      background: theme.palette.colors['bg-active']['neutral-primary'],
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      color: theme.palette.colors.bg['neutral-primary'],
      background: theme.palette.colors.bg['neutral-primary'],
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.core.white,
    },
  }),
  ...(color === 'floating' && {
    color: theme.palette.colors.content.primary,
    background: theme.palette.colors.core.white,
    boxShadow:
      '0px 0px 1px 1px rgba(26, 27, 35, 0.04), 0px 8px 8px 0px rgba(26, 27, 35, 0.06), 0px 8px 8px -8px rgba(26, 27, 35, 0.24)',
    [`&:hover`]: {
      background: theme.palette.colors.core.white,
      boxShadow:
        '0px 0px 1px 1px rgba(26, 27, 35, 0.04), 0px 2px 2px 0px rgba(26, 27, 35, 0.06), 0px 2px 2px -2px rgba(26, 27, 35, 0.24)',
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      color: theme.palette.colors.core.white,
      background: theme.palette.colors.core.white,
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.content.primary,
    },
  }),
  ...(color === 'inverted-floating' && {
    color: theme.palette.colors.core.white,
    background: theme.palette.colors.bg['neutral-primary'],
    boxShadow:
      '0px 0px 1px 1px rgba(26, 27, 35, 0.04), 0px 8px 8px 0px rgba(26, 27, 35, 0.06), 0px 8px 8px -8px rgba(26, 27, 35, 0.24)',
    [`&:hover`]: {
      background: theme.palette.colors['bg-active']['neutral-primary'],
      boxShadow:
        '0px 0px 1px 1px rgba(26, 27, 35, 0.04), 0px 2px 2px 0px rgba(26, 27, 35, 0.06), 0px 2px 2px -2px rgba(26, 27, 35, 0.24)',
    },
    [`&.${iconButtonClasses.disabled}.${iconButtonClasses.loading}`]: {
      color: theme.palette.colors.bg['neutral-primary'],
      background: theme.palette.colors.bg['neutral-primary'],
    },
    [`.${iconButtonClasses.loadingIndicator}`]: {
      color: theme.palette.colors.core.white,
    },
  }),
}));
